import type { BaseResponse, Event, FetchErrorResponse } from "@anggunpraya-templates/types";
import type { FetchError } from "ofetch";
import { useEventGuestStore } from "../stores/event-guest";
import { useTemplateStore } from "../stores/template";
import { usePackageCapabilities } from "./use-package-capabilities";
import { applyPreviewPackageModeToEventDetail, PREVIEW_PACKAGE_QUERY_KEY } from "./use-preview-package-mode";

interface UseInvitationPageOptions {
  previewEventDetail: BaseResponse<Event>;
  loadEventGuest: (code: string) => Promise<void>;
}

export function normalizeInvitationRecipientName(value: string | null | Array<string | null> | undefined) {
  const rawValue = Array.isArray(value) ? value[0] : value;

  if (!rawValue) {
    return "";
  }

  const preparedValue = rawValue.replaceAll("+", " ").trim();

  try {
    return decodeURIComponent(preparedValue).replace(/\s+/g, " ").trim();
  }
  catch {
    return preparedValue.replace(/\s+/g, " ").trim();
  }
}

export async function useInvitationPage(options: UseInvitationPageOptions) {
  const { $api } = useNuxtApp();
  const { flavor, eventCode } = useRuntimeConfig().public;
  const route = useRoute();
  const router = useRouter();

  const templateStore = useTemplateStore();
  const eventGuestStore = useEventGuestStore();
  const { requiresGuestContext } = usePackageCapabilities();

  const { template } = storeToRefs(templateStore);
  const { normalizeEventDateForPreview, setTemplateConfig } = templateStore;
  const { reset: resetEventGuest } = eventGuestStore;

  const opened = ref(false);
  const isEventPreview = ref(false);
  const invitationMode = ref<"guest" | "public">("guest");
  const recipientName = ref(normalizeInvitationRecipientName(route.query.to));
  const isGuestBased = computed(() => invitationMode.value === "guest");

  const contentRef = useTemplateRef<HTMLElement>("main");

  onMounted(() => {
    if (!opened.value) {
      document.documentElement.style.overflow = "hidden";
    }
  });

  provideInvitationContext({
    isEventPreview,
    invitationMode,
    recipientName,
    isGuestBased,
  });

  const { data: eventDetail, error, status } = await useAsyncData<BaseResponse<Event>, FetchError<FetchErrorResponse>>(
    "event-detail",
    (_nuxtApp, { signal }) => {
      if (flavor === "preview") {
        return Promise.resolve({
          ...applyPreviewPackageModeToEventDetail(
            options.previewEventDetail,
            route.query[PREVIEW_PACKAGE_QUERY_KEY],
          ),
        });
      }

      return $api("/event/detail", {
        signal,
        params: {
          subdomain: template.value.general.subdomain,
        },
      });
    },
    {
      server: true,
      immediate: true,
    },
  );

  if (error.value) {
    showError({
      data: {
        ...error.value.data?.error,
      },
    });
  }

  if (status.value === "success") {
    syncEventDateFromDetail(eventDetail.value?.data.date);
    syncInvitationMode();
    redirectIfGuestContextRequired();

    if (import.meta.client) {
      await loadEventGuestForRoute();
    }
  }

  function syncEventDateFromDetail(eventDate?: string) {
    if (!eventDate) {
      return;
    }

    setTemplateConfig({
      ...template.value,
      general: {
        ...template.value.general,
        date: eventDate,
      },
    });

    if (flavor === "preview") {
      normalizeEventDateForPreview(eventDate);
    }
  }

  function syncInvitationMode() {
    invitationMode.value = requiresGuestContext.value ? "guest" : "public";
  }

  function redirectIfGuestContextRequired() {
    if (flavor !== "event" && requiresGuestContext.value) {
      router.replace({
        path: "/",
        force: true,
      });
    }
  }

  async function loadEventGuestForRoute() {
    const code = route.params.code?.toString() || "";

    recipientName.value = normalizeInvitationRecipientName(route.query.to);

    if (!requiresGuestContext.value) {
      isEventPreview.value = false;
      resetEventGuest();
      return;
    }
    if (!code && flavor === "event") {
      showError({
        statusCode: 500,
        message: "Mohon maaf, undangan yang Anda akses bersifat pribadi.",
      });

      return;
    }

    const eventPreview = flavor === "event" && code === eventCode;
    isEventPreview.value = eventPreview;

    await options.loadEventGuest(
      eventPreview ? "EVENT_PREVIEW" : code,
    );
  }

  return {
    opened,
    contentRef,
  };
}
